/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The work inbox: what is waiting in my queues, and what I am holding.
 *
 * One list across every object type. A case, a lead and a call all arrive here
 * as work items, which is the whole point of the queue being generic -- an
 * agent should not have to check four screens to find out what to do next.
 *
 * The empty state matters more than it looks. "You are not a member of any
 * queue" is the single most common support question this feature generates, and
 * an empty table reads as a broken page rather than as a membership problem, so
 * it says so in words.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Icon,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";

const CARD_BORDER = "1px solid #E6E8F0";

/** How long an item has been waiting, in the units a person would say it in. */
function ageLabel(iso) {
  if (!iso) return "";
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function WorkItemRow({ item, onOpen, onClaim, onRelease, onComplete, busy, mine }) {
  return (
    <MDBox
      display="flex"
      alignItems="center"
      gap={1.5}
      sx={{ borderBottom: "1px solid #F2F4F8", py: 1.25 }}
    >
      <Tooltip title={item.object_label || item.object_api_name}>
        <Icon fontSize="small" color="action">
          {item.object_icon || "circle"}
        </Icon>
      </Tooltip>

      <MDBox flexGrow={1} minWidth={0}>
        <MDTypography
          variant="button"
          fontWeight="medium"
          sx={{
            cursor: item.record_url ? "pointer" : "default",
            display: "block",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          }}
          onClick={() => item.record_url && onOpen(item)}
        >
          {item.subject || `${item.object_label} #${item.record_id}`}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {item.queue_name} · waiting {ageLabel(item.created_at)}
          {item.released_count > 0 ? ` · handed back ${item.released_count}×` : ""}
        </MDTypography>
      </MDBox>

      {item.sla_breached ? (
        <Tooltip title="This has been waiting longer than the queue's target answer time.">
          <Chip size="small" color="error" variant="outlined" label="Over SLA" />
        </Tooltip>
      ) : null}

      <Tooltip title={`Priority ${item.priority} · worth ${item.weight} of your capacity`}>
        <Chip size="small" variant="outlined" label={`P${item.priority}`} />
      </Tooltip>

      {mine ? (
        <>
          <MDButton
            variant="text"
            color="dark"
            size="small"
            disabled={busy}
            onClick={() => onRelease(item)}
          >
            Hand back
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            size="small"
            disabled={busy}
            onClick={() => onComplete(item)}
          >
            Done
          </MDButton>
        </>
      ) : (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          disabled={busy}
          onClick={() => onClaim(item)}
        >
          Accept
        </MDButton>
      )}
    </MDBox>
  );
}

function WorkQueuesPage() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [tab, setTab] = useState(0); // 0 = the queue, 1 = mine
  const [items, setItems] = useState([]);
  const [myItems, setMyItems] = useState([]);

  const loadQueues = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/work/queues");
      const rows = Array.isArray(res.data) ? res.data : [];
      setQueues(rows);
      setSelectedQueue((current) => {
        if (current && rows.some((q) => q.queue_id === current)) return current;
        return rows.length ? rows[0].queue_id : null;
      });
      return rows;
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load your queues."));
      return [];
    }
  }, [axiosInstance]);

  const loadItems = useCallback(
    async (queueId) => {
      // My own items are loaded regardless of which queue is selected: they are
      // what the agent is accountable for, and hiding them behind a queue
      // selection would lose an item whose queue they later left.
      const [queueRes, mineRes] = await Promise.allSettled([
        queueId
          ? axiosInstance.get(`/work/queues/${queueId}/items`, {
              params: { status: ["pending"] },
            })
          : Promise.resolve({ data: [] }),
        axiosInstance.get("/work/items"),
      ]);
      setItems(
        queueRes.status === "fulfilled" && Array.isArray(queueRes.value.data)
          ? queueRes.value.data
          : []
      );
      setMyItems(
        mineRes.status === "fulfilled" && Array.isArray(mineRes.value.data)
          ? mineRes.value.data
          : []
      );
      if (queueRes.status === "rejected") {
        setError(getApiErrorMessage(queueRes.reason, "Unable to load this queue's items."));
      }
    },
    [axiosInstance]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    const rows = await loadQueues();
    const queueId =
      selectedQueue && rows.some((q) => q.queue_id === selectedQueue)
        ? selectedQueue
        : rows.length
        ? rows[0].queue_id
        : null;
    await loadItems(queueId);
    setLoading(false);
  }, [loadQueues, loadItems, selectedQueue]);

  useEffect(() => {
    refresh();
    // Deliberately once on mount. A polling inbox that refreshes under an
    // agent's cursor moves the row they were about to click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedQueue) loadItems(selectedQueue);
  }, [selectedQueue, loadItems]);

  const act = async (verb, item, body = {}) => {
    setBusy(true);
    setError("");
    try {
      await axiosInstance.post(`/work/items/${item.id}/${verb}`, body);
      setToast(
        {
          claim: "It's yours.",
          release: "Handed back to the queue.",
          complete: "Marked done.",
        }[verb]
      );
      await loadQueues();
      await loadItems(selectedQueue);
    } catch (err) {
      // 409 is the expected outcome when somebody else got there first, and it
      // deserves the server's wording rather than a generic failure.
      setError(getApiErrorMessage(err, "That didn't work."));
      await loadItems(selectedQueue);
    } finally {
      setBusy(false);
    }
  };

  const openRecord = (item) => {
    if (item.record_url) navigate(item.record_url);
  };

  const active = tab === 0 ? items : myItems;
  const totalMine = myItems.length;
  const selected = useMemo(
    () => queues.find((q) => q.queue_id === selectedQueue),
    [queues, selectedQueue]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <MDBox flexGrow={1}>
            <MDTypography variant="h5" fontWeight="medium">
              Work queues
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Everything waiting for you, whatever kind of record it is.
            </MDTypography>
          </MDBox>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </MDBox>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={26} />
          </MDBox>
        ) : queues.length === 0 ? (
          <Card sx={{ p: 4, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
            <MDTypography variant="h6" fontWeight="medium" mb={1}>
              You are not a member of any queue
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Work queues only show records from queues you belong to. Ask an administrator to add
              you to one in Settings → Queues.
            </MDTypography>
          </Card>
        ) : (
          <MDBox display="flex" gap={3} alignItems="flex-start" flexWrap="wrap">
            {/* Queue rail */}
            <Card
              sx={{
                p: 2,
                border: CARD_BORDER,
                boxShadow: "none",
                borderRadius: "12px",
                minWidth: 240,
                flexShrink: 0,
              }}
            >
              <MDTypography variant="button" fontWeight="medium" color="text">
                My queues
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              {queues.map((q) => (
                <MDBox
                  key={q.queue_id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    px: 1,
                    py: 0.75,
                    bgcolor: q.queue_id === selectedQueue ? "#F2F6FF" : "transparent",
                  }}
                  onClick={() => setSelectedQueue(q.queue_id)}
                >
                  <MDBox flexGrow={1} minWidth={0}>
                    <MDTypography variant="button" fontWeight="medium" noWrap display="block">
                      {q.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {q.object_api_names.length
                        ? q.object_api_names.join(", ")
                        : "no object scope"}
                    </MDTypography>
                  </MDBox>
                  {q.pending_items ? (
                    <Chip size="small" color="info" label={q.pending_items} />
                  ) : null}
                </MDBox>
              ))}
            </Card>

            {/* Items */}
            <Card
              sx={{
                p: 3,
                border: CARD_BORDER,
                boxShadow: "none",
                borderRadius: "12px",
                flexGrow: 1,
                minWidth: 420,
              }}
            >
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
                <Tab label={`Waiting${selected ? ` in ${selected.name}` : ""}`} />
                <Tab label={`Mine (${totalMine})`} />
              </Tabs>

              {active.length === 0 ? (
                <MDTypography variant="body2" color="text" py={3}>
                  {tab === 0
                    ? "Nothing waiting in this queue."
                    : "You are not holding any work right now."}
                </MDTypography>
              ) : (
                active.map((item) => (
                  <WorkItemRow
                    key={item.id}
                    item={item}
                    mine={tab === 1}
                    busy={busy}
                    onOpen={openRecord}
                    onClaim={(i) => act("claim", i)}
                    onRelease={(i) => act("release", i)}
                    onComplete={(i) => act("complete", i)}
                  />
                ))
              )}
            </Card>
          </MDBox>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}

export default WorkQueuesPage;
